import { useEffect, useState } from 'react';
import {
    CampaignIdApiResponse,
    RecallsApiResponse,
} from '../../../../services/recalls-service/recalls-service';
import ServiceHandler from '../../../../services/service-handler';
import { notify } from '../../../../services/slack-message-service/SlackMessageService';
import { RECALLS } from '../../../../support/support-constants';
import isWebviewCheck from '../../../utils/webviewCheck/webviewCheck';

export interface RecallsApiResponseWithLoading {
    recallData: RecallsApiResponse | null;
    loading: boolean;
    loadTime: number;
}

export const useRecallData = (
    vin?: string,
    model?: string,
    year?: string
): RecallsApiResponseWithLoading | null => {
    const [recallData, setRecallData] = useState<RecallsApiResponse | null>(
        null
    );
    const [loading, setLoading] = useState<boolean>(false);
    const [loadTime, setLoadTime] = useState<number>(0);
    const callVin = (vin: string, timeout?: boolean) => {
        ServiceHandler.RecallsService.request(vin, timeout)
            .then(recalls => {
                recalls && setRecallData(recalls);
            })
            .catch(error => {
                notify(
                    'Failed to retrieve data for recall tile due to an upstream failure for VIN: ' +
                        vin +
                        '.' +
                        error.message,
                    RECALLS.SLACK_ALERT_RECALLS_AEM_API
                );

                if (error.message === 'Request Timed Out') {
                    callVin(vin, false);
                } else {
                    setRecallData({
                        successfulResponse: false,
                    } as RecallsApiResponse);
                }
            })
            .finally(() => {
                setLoading(false);
            });
    };
    const isWebview = isWebviewCheck();

    useEffect(() => {
        if (year && model) {
            const startTime = performance.now();
            setLoading(true);
            ServiceHandler.RecallsService.requestYmm(model, year)
                .then(recalls => {
                    recalls && setRecallData(recalls);
                })
                .catch(error => {
                    notify(
                        'Failed to retrieve data for recall tile due to an upstream failure for Year and Model: ' +
                            year +
                            ' ' +
                            model +
                            ' ' +
                            '.' +
                            error.message,
                        RECALLS.SLACK_ALERT_RECALLS_AEM_API
                    );
                    setRecallData({
                        successfulResponse: false,
                    } as RecallsApiResponse);
                })
                .finally(() => {
                    setLoading(false);
                });

            const endTime = performance.now();
            const currentLoadTime = endTime - startTime;
            setLoadTime(currentLoadTime);
        }
    }, [year, model]);

    useEffect(() => {
        if (vin) {
            const startTime = performance.now();
            setLoading(true);
            callVin(vin, isWebview);
            const endTime = performance.now();
            const currentLoadTime = endTime - startTime;
            setLoadTime(currentLoadTime);
        }
    }, [vin]);
    return { recallData, loading, loadTime };
};

export const useCampaignIdData = (
    id?: string
): CampaignIdApiResponse | null => {
    const [
        campaignIdData,
        setCampaignIdData,
    ] = useState<CampaignIdApiResponse | null>(null);
    useEffect(() => {
        if (id) {
            ServiceHandler.RecallsService.getCampaignId(id.toUpperCase())
                .then(response => {
                    response && setCampaignIdData(response);
                })
                .catch(error => {
                    setCampaignIdData({
                        error: error,
                    } as CampaignIdApiResponse);
                });
        }
    }, [id]);

    return campaignIdData;
};
